import { Head, Link, router } from '@inertiajs/react';
import React, { useState, useRef } from 'react';
import { SimpleFileUpload } from 'simple-file-upload-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, FileText, Image, Film, Music, Archive, File, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Upload({ bucket }) {
  const [tag, setTag] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileUploadRef = useRef(null);

  const handleFileChange = (event) => {
    
    // Check if user clicked "Done"
    if (event.action === 'uploadDone') {
      // Reset the uploader
      if (fileUploadRef.current?.reset) {
        fileUploadRef.current.reset();
      }
      return;
    }
    
    // Track successful uploads - simple append only approach
    if (event.allFiles) {
      const successfulFiles = event.allFiles.filter(file => file.cdnUrl && file.status === 'success');
      
      successfulFiles.forEach(file => {
        // Only add if not already in the list
        const exists = uploadedFiles.some(f => f.cdn_url === file.cdnUrl);
        if (!exists) {
          const newFile = {
            id: file.id || `${file.name}-${Date.now()}`,
            file_name: file.name,
            cdn_url: file.cdnUrl,
            content_type: file.type || 'application/octet-stream',
            tag: tag || "No tag"
          };
          
          setUploadedFiles(prev => [...prev, newFile]);
          toast.success(`${file.name} uploaded successfully!`);
        }
      });
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDeleteFile = (file) => {
    if (!confirm(`Are you sure you want to delete ${file.file_name}?`)) {
      return;
    }

    // Use Inertia router to delete by CDN URL
    router.delete('/react_images/delete_by_url', {
      data: { 
        cdn_url: file.cdn_url,
        from_upload_page: true  // Flag to tell controller this came from upload page
      },
      onSuccess: (page) => {
        // Check if the deletion was actually successful by looking for success flash
        if (page.props.flash?.notice && page.props.flash.notice.includes('deleted successfully')) {
          setUploadedFiles(prev => prev.filter(f => f.cdn_url !== file.cdn_url));
        }
        // For errors, the file stays in the list and flash message shows the error
      },
      onError: () => {
        // Handle unexpected errors silently
      }
    });
  };

  return (
    <div className="min-h-screen">
      <Head title="Upload Your Own Files" />
      
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Upload Your Own Files</h1>
          <p className="text-xl text-gray-600 mb-6">
            Upload files to use in your web application. All files are instantly available in your dashboard.
          </p>
          
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl">
            <p className="text-sm font-medium text-blue-900 mb-2">How it works:</p>
            <ul className="space-y-1.5 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Upload most file types - images, documents, and other common assets</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Files are instantly available with a unique URL for your applications</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Organize files with tags for easy management</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Settings</h2>
              
              <div className="space-y-4">
                {/* Tag Input */}
                <div>
                  <Label htmlFor="tag" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Tag
                  </Label>
                  <Input
                    id="tag"
                    type="text"
                    placeholder="e.g., marketing-assets, product-images"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Add tags to organize your files
                  </p>
                </div>

                {/* Uploader Component */}
                <div className="pt-4 w-full max-w-full [&_*]:outline-none [&_*]:border-none [&_*:focus]:outline-none [&_*:focus]:border-none [&_*:focus]:ring-0 [&_*:focus-visible]:outline-none [&_*:focus-visible]:ring-0 [&_*:active]:outline-none [&_*:active]:ring-0">
                  <SimpleFileUpload
                    ref={fileUploadRef}
                    publicKey={bucket.public_key}
                    onChange={handleFileChange}
                    onReset={() => {
                      // This fires when component resets itself
                    }}
                    multiple={true}
                    tag={tag || undefined}
                  />
                </div>
                
                <p className="text-sm text-gray-600 text-center mt-4">
                  Need to edit images? Try our <Link href="/edit-images" className="text-blue-600 hover:text-blue-800 underline">Image Editor</Link>
                </p>
              </div>
            </div>
          </div>

          {/* Files List */}
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Uploaded Files {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                View and manage all files in your <Link href="/dashboard" className="text-blue-600 hover:text-blue-800 underline">Dashboard</Link>
              </p>
            </div>

            {uploadedFiles.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No files uploaded yet</h3>
                <p className="text-gray-500">Upload files to see them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          {file.content_type?.startsWith('image/') ? (
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100">
                              <img 
                                src={`${file.cdn_url}?w=40&h=40`} 
                                alt={file.file_name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                              <File className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate" title={file.file_name}>
                            {file.file_name}
                          </p>
                          {file.tag && file.tag !== "No tag" && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {file.tag}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(file.cdn_url)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy URL
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteFile(file)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}